import { Button, Dialog, DialogContent, DialogTitle, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Slide, Stack, Switch, TextField, Typography } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { DatePicker } from "@mui/x-date-pickers";
import { useSnackbar } from "notistack";
import React, { ReactElement } from "react";
import { ApiError } from "../../Api/ApiError";
import { getChargeCategoryList, getChargeData, regChargeData } from "../../Api/Store";
import { WeekLongString, WeekType } from "../../Helper/Week";
import { ChargeCategory } from "../../Objects/ChargeCategory";
import { ChargePattern } from "../../Objects/ChargePattern";
import TimeInput from "../atoms/TimeInput";
import ChargeCategoryText from "../atoms/ChargeCategoryText";


const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />
})

export interface ChargePatternEditDialogProps {
    open: boolean
    chrptnId?: number
    onClose: (isEdit: boolean) => void
    str_id: number
    std_id: number
}

const ChargePatternEditDialog: React.FC<ChargePatternEditDialogProps> = ({
    open,
    chrptnId,
    onClose,
    str_id,
    std_id,
}) => {
    let { enqueueSnackbar } = useSnackbar()

    const [dialogOpen, setDialogOpen] = React.useState<boolean>(false)
    const [chargePattern, setChargePattern] = React.useState<ChargePattern>({ str_id: str_id, std_id: std_id })
    const [chargeCategoryList, setChargeCategoryList] = React.useState<ChargeCategory[]>()
    const [disableSubmit, setDisableSubmit] = React.useState<boolean>(true)

    const abortController = React.useMemo(() => new AbortController(), [])

    React.useEffect(() => {
        return () => {
            abortController.abort()
        }
    }, [abortController])

    const doSave = React.useCallback(() => {
        regChargeData({
            chargePattern: chargePattern
        }, {signal: abortController.signal}).then(result => {
            onClose(true)
            enqueueSnackbar("変更しました", { variant: "success" })
        }).catch((e: any) => {
            if( e.__CANCEL__ ) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            }else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
    }, [abortController, chargePattern, enqueueSnackbar, onClose])

    React.useEffect(() => {
        if (!chargePattern.chrcate_id
            || !chargePattern.start
            || !chargePattern.end
            || !chargePattern.chrptn_charge) {
            setDisableSubmit(true)
        } else {
            setDisableSubmit(false)
        }
    }, [chargePattern])

    React.useEffect(() => {
        const controller = new AbortController()
        getChargeCategoryList({
            "access_key": "HEAD-OFFICE_LOGIN"
        }, {signal: controller.signal}).then(result => {
            setChargeCategoryList(result.chargeCategoryList)
        }).catch((e: any) => {
            if( e.__CANCEL__ ) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            }else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
        return () => {
            controller.abort()
        }
    }, [enqueueSnackbar])

    React.useEffect(() => {
        const controller = new AbortController()
        if (open) {
            if (chrptnId) {
                getChargeData({
                    chrptn_id: chrptnId
                }, {signal: controller.signal}).then(value => {
                    setChargePattern(value.chargePattern)
                    setDialogOpen(true)
                }).catch((e: any) => {
                    if( e.__CANCEL__ ) {
                        console.info("canceled")
                    } else if (e instanceof ApiError) {
                        enqueueSnackbar(e.message, { variant: "error" })
                    } else if (e instanceof Error) {
                        enqueueSnackbar(e.message, { variant: "error" })
                    }else {
                        enqueueSnackbar("通信に失敗しました", { variant: "error" })
                    }
                })
            } else {
                setChargePattern({ str_id: str_id, std_id: std_id })
                setDialogOpen(true)
            }
        } else {
            setDialogOpen(false)
        }
    }, [open, chrptnId, str_id, std_id, enqueueSnackbar])


    return (
        <Dialog
            open={dialogOpen}
            TransitionComponent={Transition}
            onClose={() => {
                onClose(false)
            }}
            fullWidth
        >
            <DialogTitle>スタジオ料金設定を変更</DialogTitle>
            <DialogContent
                sx={{
                    overflowY: "visible"
                }}
            >
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3.5} sx={{
                        textAlign: {
                            md: "right",
                            xs: "left"
                        },
                        paddingRight: 2
                    }}>
                        <Typography variant="caption" sx={{ color: "#333399" }}>適用開始日</Typography>
                    </Grid>
                    <Grid item xs={12} md={8.5}>
                        <DatePicker
                            label="適用開始日"
                            onChange={date => {
                                let cp: ChargePattern = {
                                    ...chargePattern,
                                    start_date: date ? date : undefined
                                }
                                setChargePattern(cp)
                            }}
                            value={chargePattern.start_date ? chargePattern.start_date : null}
                            renderInput={params => <TextField {...params} fullWidth size="small" />}
                            inputFormat="yyyy/MM/dd"
                            mask="____/__/__"
                            clearable={true}
                            defaultCalendarMonth={null}
                        />
                    </Grid>
                    <Grid item xs={12} md={3.5} sx={{
                        textAlign: {
                            md: "right",
                            xs: "left"
                        },
                        paddingRight: 2
                    }}>
                        <Typography variant="caption" sx={{ color: "#333399" }}>適用終了日</Typography>
                    </Grid>
                    <Grid item xs={12} md={8.5}>
                        <DatePicker
                            label="適用終了日"
                            onChange={date => {
                                let cp: ChargePattern = {
                                    ...chargePattern,
                                    end_date: date ? date : undefined
                                }
                                setChargePattern(cp)
                            }}
                            value={chargePattern.end_date ? chargePattern.end_date : null}
                            renderInput={params => <TextField {...params} fullWidth size="small" />}
                            inputFormat="yyyy/MM/dd"
                            mask="____/__/__"
                            clearable={true}
                            defaultCalendarMonth={null}
                        />
                    </Grid>
                    <Grid item xs={12} md={3.5} sx={{
                        textAlign: {
                            md: "right",
                            xs: "left"
                        },
                        paddingRight: 2
                    }}>
                        <Typography variant="caption" sx={{ color: "#333399" }}>料金カテゴリ</Typography>
                    </Grid>
                    <Grid item xs={12} md={8.5}>
                        <FormControl
                            sx={{
                                width: "100%"
                            }}
                            size="small"
                        >
                            <InputLabel id="chargeCategory">料金カテゴリ</InputLabel>
                            <Select
                                labelId="chargeCategory"
                                value={chargePattern.chrcate_id ? chargePattern.chrcate_id : 0}
                                onChange={(e: SelectChangeEvent<number>) => {
                                    let val = typeof e.target.value == "string" ? parseInt(e.target.value) : e.target.value
                                    let cp: ChargePattern = {
                                        ...chargePattern,
                                        chrcate_id: val === 0 ? undefined : val
                                    }
                                    setChargePattern(cp)
                                }}
                                label="料金カテゴリ"
                                fullWidth
                                size="small"
                            >
                                <MenuItem value={0} disabled />
                                {chargeCategoryList?.map(chargeCategory => {
                                    return <MenuItem value={chargeCategory.chrcate_id} key={chargeCategory.chrcate_id}>
                                        <ChargeCategoryText chargeCategory={chargeCategory} />
                                    </MenuItem>
                                })}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3.5} sx={{
                        textAlign: {
                            md: "right",
                            xs: "left"
                        },
                        paddingRight: 2
                    }}>
                        <Typography variant="caption" sx={{ color: "#333399" }}>適用曜日</Typography>
                    </Grid>
                    <Grid item xs={12} md={8.5}>
                        {Object.keys(WeekLongString).map(key => (
                            <FormControlLabel
                                key={key}
                                control={<Switch
                                    color="secondary"
                                    checked={chargePattern.week_conditions ? chargePattern.week_conditions.includes(key as WeekType) : false}
                                    onChange={e => {
                                        let weekConditions: WeekType[] = chargePattern.week_conditions ? chargePattern.week_conditions.filter(value => value !== key as WeekType) : []
                                        if (e.target.checked) {
                                            weekConditions.push(key as WeekType)
                                        }
                                        let cp: ChargePattern = {
                                            ...chargePattern,
                                            week_conditions: weekConditions.length > 0 ? weekConditions : undefined
                                        }
                                        setChargePattern(cp)
                                    }}
                                />}
                                label={WeekLongString[key as WeekType]}
                            />
                        ))}
                    </Grid>
                    <Grid item xs={12} md={3.5} sx={{
                        textAlign: {
                            md: "right",
                            xs: "left"
                        },
                        paddingRight: 2
                    }}>
                        <Typography variant="caption" sx={{ color: "#333399" }}>適用時間</Typography>
                    </Grid>
                    <Grid item xs={12} md={8.5}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                        >
                            <TimeInput
                                value={chargePattern.start ? chargePattern.start : null}
                                onChange={value => {
                                    let cp: ChargePattern = {
                                        ...chargePattern,
                                        start: value ? value : undefined
                                    }
                                    setChargePattern(cp)
                                }}
                            />
                            <Typography>～</Typography>
                            <TimeInput
                                value={chargePattern.end ? chargePattern.end : null}
                                onChange={value => {
                                    let cp: ChargePattern = {
                                        ...chargePattern,
                                        end: value ? value : undefined
                                    }
                                    setChargePattern(cp)
                                }}
                            />

                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={3.5} sx={{
                        textAlign: {
                            md: "right",
                            xs: "left"
                        },
                        paddingRight: 2
                    }}>
                        <Typography variant="caption" sx={{ color: "#333399" }}>料金</Typography>
                    </Grid>
                    <Grid item xs={12} md={8.5}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                        >
                            <TextField
                                inputProps={{
                                    inputMode: 'numeric',
                                    pattern: '[1-9][0-9]*'
                                }}
                                value={chargePattern.chrptn_charge ? chargePattern.chrptn_charge : ""}
                                onChange={e => {
                                    let cp: ChargePattern = {
                                        ...chargePattern,
                                        chrptn_charge: e.target.value ? parseInt(e.target.value) : undefined
                                    }
                                    setChargePattern(cp)
                                }}
                                size="small"
                            />
                            <Typography>円/1コマ</Typography>
                        </Stack>
                    </Grid>
                    <Grid item
                        sx={{
                            mx: 0,
                            mt: 2,
                            marginLeft: "auto",
                            textAlign: "center",
                        }}
                        xs={12}
                    >
                        <Stack
                            spacing={1}
                            direction="row"
                            justifyContent="right"
                        >
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    doSave()
                                    // doSubmit()
                                }}
                                disabled={disableSubmit}
                            >保存</Button>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    onClose(false)
                                }}
                            >キャンセル</Button>
                        </Stack>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
}

export default ChargePatternEditDialog